import {
  TeText,
  TeButton,
  TeCell,
  TeCheckbox,
  TeRow,
  TeTable,
  TeContextMenu,
  TeContextMenuControl,
  TeContextMenuItem,
} from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';
import { Dispatch, SetStateAction, useState } from 'react';
import { styled } from 'styled-components';
import { formatDateTime } from '@topcon/topcon-utils';

import HeaderCell from '../HeaderCell';
import { RequestToJoinBody } from '../../../api/generated/pap';

interface Props {
  requests: RequestToJoinBody[];
  selectedIds: string[];
  setSelectedIds: Dispatch<SetStateAction<string[]>>;
  setAcceptRequestModalOpen: Dispatch<SetStateAction<boolean>>;
  setDeclineRequestModalOpen: Dispatch<SetStateAction<boolean>>;
}

function RequestsTable({
  requests,
  selectedIds,
  setSelectedIds,
  setDeclineRequestModalOpen,
  setAcceptRequestModalOpen,
}: Props) {
  const [sortKey, setSortKey] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<string>('asc');
  const { t } = useTranslation('users');
  // TODO: Not much data to test this, validate later
  let sortedRequests = requests.sort((a, b) => {
    const aName = a?.identity?.firstName ?? '';
    const bName = b?.identity?.firstName ?? '';
    // Otherwise sort by name as default
    return aName.localeCompare(bName ?? '', 'fi', { numeric: true });
  });
  if (sortDirection === 'desc') sortedRequests = sortedRequests.reverse();

  const isIndeterminate = selectedIds.length > 0 && selectedIds.length < requests.length;
  const isAllSelected = selectedIds.length === requests.length;

  const toggleUserSelect = (selectedID: string) => {
    setSelectedIds((prev) =>
      prev.includes(selectedID) ? prev.filter((id) => id !== selectedID) : [...prev, selectedID]
    );
  };

  const toggleAll = () => {
    setSelectedIds(() => (isAllSelected ? [] : requests.map(({ id }) => id)));
  };

  const handleSortChange = (key: string) => {
    const isSame = sortKey === key;
    if (!isSame) setSortKey(key);
    setSortDirection((prev) => {
      if (!isSame) return 'asc';
      return prev === 'asc' ? 'desc' : 'asc';
    });
  };

  const handleAcceptRequestAction = (id: string) => {
    setSelectedIds([id]);
    setAcceptRequestModalOpen(true);
  };

  const handleDeclineRequestAction = (id: string) => {
    setSelectedIds([id]);
    setDeclineRequestModalOpen(true);
  };

  return (
    <Table evenOdd nested>
      <TeRow>
        <TeCell justifyContent="center" noPadding compact>
          <TeCheckbox
            checked={isIndeterminate || isAllSelected}
            indeterminate={isIndeterminate}
            techange={toggleAll}
            wrapperSize={40}
          />
        </TeCell>
        <HeaderCell
          onHeaderClick={handleSortChange}
          sortKey={sortKey}
          sortDirection={sortDirection}
          name={t('name')}
          cellKey="name"
        />
        <HeaderCell
          onHeaderClick={handleSortChange}
          sortKey={sortKey}
          sortDirection={sortDirection}
          name={t('email')}
          cellKey="email"
        />
        <HeaderCell
          onHeaderClick={handleSortChange}
          sortKey={sortKey}
          sortDirection={sortDirection}
          name={t('requestedAt')}
          cellKey="requestedAt"
        />
        <TeCell compact />
      </TeRow>

      {sortedRequests.map(({ id, createdAt, identity }) => {
        if (!identity) return null;
        const { firstName, lastName, email } = identity;
        const selected = selectedIds.includes(id);
        return (
          <TeRow key={id} active={selected}>
            <TeCell justifyContent="center" noPadding compact>
              <TeCheckbox
                checked={selected}
                techange={() => toggleUserSelect(id)}
                wrapperSize={40}
              />
            </TeCell>
            <TeCell>
              <TeText weight={500}>{`${firstName} ${lastName}`}</TeText>
            </TeCell>
            <TeCell>
              <TeText>{email}</TeText>
            </TeCell>
            <TeCell>{createdAt && <TeText>{formatDateTime(new Date(createdAt))}</TeText>}</TeCell>
            <TeCell justifyContent="center" compact>
              {/* TODO: This means a ton of unnecessary junk */}
              <TeContextMenuControl contextId={`options-${id}`}>
                <TeButton
                  icon="more_vert"
                  backgroundColor="transparent"
                  iconColor="icon.secondary"
                  size="s"
                />
              </TeContextMenuControl>
              <TeContextMenu contextId={`options-${id}`} size="s">
                <TeContextMenuItem
                  text={t('accept')}
                  teclick={() => handleAcceptRequestAction(id)}
                />
                <TeContextMenuItem
                  text={t('decline')}
                  teclick={() => handleDeclineRequestAction(id)}
                />
              </TeContextMenu>
            </TeCell>
          </TeRow>
        );
      })}
    </Table>
  );
}

export default RequestsTable;

const Table = styled(TeTable)`
  border-radius: 0;
`;
